import React, { useState, useEffect } from 'react';
import apiWithAuth from '../../services/apiWithAuth';
import InvoicePreviewModal from '../common/InvoicePreviewModal';
import {
  Download,
  IndianRupee,
  Loader2,
  X,
  Check,
  AlertTriangle,
  CalendarDays,
  Store,
  Users,
  TrendingUp,
  FileText,
  Eye,
  CreditCard,
  Package,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';

const INVOICES_PER_PAGE = 10;

// Tab types
const TAB_INVOICES = 'invoices';
const TAB_SUBSCRIPTION_ORDERS = 'subscription-orders';

const formatCurrency = (amount) => {
  if (typeof amount !== 'number' || isNaN(amount) || amount === 0) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR'
  }).format(amount);
};

const KDPInvoices = () => {
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [shopId, setShopId] = useState('');
  const [shops, setShops] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [authToken] = useState(localStorage.getItem('token'));
  const [downloadingSubscriptions, setDownloadingSubscriptions] = useState(new Set());

  // Tab state
  const [activeTab, setActiveTab] = useState(TAB_INVOICES);

  // Subscription orders state
  const [subscriptionOrders, setSubscriptionOrders] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState('');
  const [subscriptionShopFilter, setSubscriptionShopFilter] = useState('');

  // Invoice modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceHtml, setInvoiceHtml] = useState('');
  const [loadingInvoices, setLoadingInvoices] = useState(new Set());
  const [loadingSubscriptionInvoices, setLoadingSubscriptionInvoices] = useState(new Set());

  // Fetch shops on mount
  useEffect(() => {
    const fetchShops = async () => {
      try {
        const response = await apiWithAuth.get('/admin-panel/shops');
        if (response.data.success) {
          const shopsData = response.data.data || [];
          // Map the shops to have consistent shopName field
          const mappedShops = shopsData.map(shop => ({
            ...shop,
            shopName: shop.name || shop.shopName
          }));
          setShops(mappedShops);
        }
      } catch (error) {
        console.error('Failed to fetch shops:', error);
      }
    };

    if (authToken) {
      fetchShops();
    }
  }, [authToken]);

  // Fetch invoices
  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        startDate: dateRange.start,
        endDate: dateRange.end,
        page: page,
        limit: INVOICES_PER_PAGE
      };

      if (shopId) {
        params.shopId = shopId;
      }

      const response = await apiWithAuth.get('/kdp-invoices', { params });

      if (response.data.success) {
        setInvoices(response.data.data.orders || []);
        setSummary(response.data.data.summary || null);
        setTotalPages(response.data.data.pagination.totalPages);
        setCurrentPage(response.data.data.pagination.currentPage);
        setTotalRecords(response.data.data.pagination.totalRecords);
      } else {
        setError(response.data.message || 'Failed to fetch invoices');
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch invoices';
      setError(errorMessage);
      setInvoices([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    console.log('Mount effect - authToken:', !!authToken);
    if (authToken) {
      fetchInvoices(1);
    }
  }, [authToken]);

  // Fetch subscription orders
  const fetchSubscriptionOrders = async (page = 1) => {
    setSubscriptionLoading(true);
    setSubscriptionError(null);

    try {
      const params = {
        page: page,
        limit: 20
      };

      if (subscriptionShopFilter) {
        params.shopId = subscriptionShopFilter;
      }

      if (subscriptionStatusFilter) {
        params.status = subscriptionStatusFilter;
      }

      const response = await apiWithAuth.get('/kdp-invoices/subscription-orders', { params });

      if (response.data.success) {
        setSubscriptionOrders(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
        setCurrentPage(response.data.pagination?.page || 1);
        setTotalRecords(response.data.pagination?.total || 0);
      } else {
        setSubscriptionError(response.data.message || 'Failed to fetch subscription orders');
      }
    } catch (error) {
      console.error('Failed to fetch subscription orders:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch subscription orders';
      setSubscriptionError(errorMessage);
      setSubscriptionOrders([]);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Fetch subscription orders on mount or when filters change
  useEffect(() => {
    if (authToken && activeTab === TAB_SUBSCRIPTION_ORDERS) {
      fetchSubscriptionOrders(1);
    }
  }, [authToken, activeTab]);

  // Fetch subscription orders when filters change
  useEffect(() => {
    if (authToken && activeTab === TAB_SUBSCRIPTION_ORDERS) {
      fetchSubscriptionOrders(1);
    }
  }, [subscriptionShopFilter, subscriptionStatusFilter]);

  // Fetch when filters change
  useEffect(() => {
    console.log('Filter change effect - dateRange:', dateRange, 'shopId:', shopId);
    if (authToken) {
      fetchInvoices(1);
    }
  }, [dateRange.start, dateRange.end, shopId, authToken]);

  // Fetch when page changes
  useEffect(() => {
    console.log('Page change effect - currentPage:', currentPage);
    if (authToken && currentPage > 1) {
      fetchInvoices(currentPage);
    }
  }, [currentPage, authToken]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const downloadInvoice = async (invoiceId) => {
    setLoadingInvoices(prev => new Set(prev).add(invoiceId));
    try {
      const response = await apiWithAuth.get('/kdp-invoices/shop-invoice', {
        params: {
          orderId: invoiceId,
          onlyHtml: true
        }
      });

      // API returns HTML in response.data.data
      const htmlContent = response.data.data || response.data;
      setInvoiceHtml(htmlContent);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error loading invoice:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load invoice';
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setLoadingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoiceId);
        return newSet;
      });
    }
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setInvoiceHtml('');
  };

  // Download subscription invoice function (for PDF download)
  const downloadSubscriptionInvoicePdf = async (subscriptionOrder) => {
    setDownloadingSubscriptions(prev => new Set([...prev, subscriptionOrder._id]));
    try {
      const params = {
        subscriptionOrderId: subscriptionOrder._id,
        onlyHtml: false
      };

      // If razorpayOrderId exists, include it
      if (subscriptionOrder.razorpayOrderId) {
        params.razorpayOrderId = subscriptionOrder.razorpayOrderId;
      }

      const response = await apiWithAuth.get('/kdp-invoices/subscription-invoice', { params, responseType: 'blob' });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `subscription-invoice-${subscriptionOrder._id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessage('Invoice downloaded successfully');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error downloading subscription invoice:', error);
      const errorMessage = error.response?.data?.message || 'Failed to download subscription invoice';
      setSubscriptionError(errorMessage);
      setTimeout(() => setSubscriptionError(null), 5000);
    } finally {
      setDownloadingSubscriptions(prev => {
        const newSet = new Set(prev);
        newSet.delete(subscriptionOrder._id);
        return newSet;
      });
    }
  };

  // View subscription invoice function (for HTML preview)
  const viewSubscriptionInvoice = async (subscriptionOrder) => {
    setLoadingSubscriptionInvoices(prev => new Set(prev).add(subscriptionOrder._id));
    try {
      const params = {
        subscriptionOrderId: subscriptionOrder._id,
        onlyHtml: true
      };

      // If razorpayOrderId exists, include it
      if (subscriptionOrder.razorpayOrderId) {
        params.razorpayOrderId = subscriptionOrder.razorpayOrderId;
      }

      const response = await apiWithAuth.get('/kdp-invoices/subscription-invoice', { params });

      // API returns HTML in response.data.data
      const htmlContent = response.data.data || response.data;
      setInvoiceHtml(htmlContent);
      setShowInvoiceModal(true);
    } catch (error) {
      console.error('Error loading subscription invoice:', error);
      const errorMessage = error.response?.data?.message || 'Failed to load subscription invoice';
      setSubscriptionError(errorMessage);
      setTimeout(() => setSubscriptionError(null), 5000);
    } finally {
      setLoadingSubscriptionInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(subscriptionOrder._id);
        return newSet;
      });
    }
  };

  if (authToken === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!authToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="font-bold">Authentication Required</p>
            <p>Please login to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {showInvoiceModal && (
        <InvoicePreviewModal htmlContent={invoiceHtml} onClose={closeInvoiceModal} />
      )}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-8 w-8 text-blue-600" />
            KDP Invoices
          </h1>
          <p className="mt-1 text-sm text-gray-500">View and manage all KDP invoices from delivered orders and subscription orders</p>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setActiveTab(TAB_INVOICES)}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                activeTab === TAB_INVOICES
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Invoices
              </div>
            </button>
            <button
              onClick={() => setActiveTab(TAB_SUBSCRIPTION_ORDERS)}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
                activeTab === TAB_SUBSCRIPTION_ORDERS
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Subscription Orders
              </div>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {activeTab === TAB_INVOICES && summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{summary.dateRange ? summary.totalOrders : 0}</p>
                </div>
                <Store className="h-8 w-8 text-blue-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Order Value</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.totalOrderValue)}</p>
                </div>
                <IndianRupee className="h-8 w-8 text-green-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Platform Fees</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.financials?.platformFees)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-orange-500 opacity-20" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Earnings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.financials?.totalEarnings)}</p>
                </div>
                <Users className="h-8 w-8 text-purple-500 opacity-20" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {activeTab === TAB_INVOICES && (
          <div className="bg-white shadow rounded-lg mb-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* From Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <CalendarDays className="h-4 w-4 text-gray-500" />
                  From Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md py-2 px-3"
                />
              </div>

              {/* To Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <CalendarDays className="h-4 w-4 text-gray-500" />
                  To Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md py-2 px-3"
                />
              </div>

              {/* Shop Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Store className="h-4 w-4 text-gray-500" />
                  Shop
                </label>
                <select
                  value={shopId}
                  onChange={(e) => setShopId(e.target.value)}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white text-gray-900 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Shops</option>
                  {shops.map((shop) => (
                    <option key={shop._id} value={shop._id}>
                      {shop.shopName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Total Records */}
              <div className="flex items-end">
                <div className="w-full">
                  <p className="text-sm font-medium text-gray-700 mb-1">Total Records</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {loading ? <Loader2 className="inline-block animate-spin h-5 w-5" /> : totalRecords}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 flex items-center justify-between" role="alert">
            <div>
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 flex items-center justify-between" role="alert">
            <div className="flex items-center">
              <Check className="mr-2" size={20} />
              <span className="font-medium">{successMessage}</span>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-green-700 hover:text-green-900"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Subscription Orders Error Message */}
        {subscriptionError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 flex items-center justify-between" role="alert">
            <div>
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {subscriptionError}</span>
            </div>
            <button
              onClick={() => setSubscriptionError(null)}
              className="text-red-700 hover:text-red-900"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Subscription Orders Filters (shown only when subscription tab is active) */}
        {activeTab === TAB_SUBSCRIPTION_ORDERS && (
          <div className="bg-white shadow rounded-lg mb-6 p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Shop Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Store className="h-4 w-4 text-gray-500" />
                  Shop
                </label>
                <select
                  value={subscriptionShopFilter}
                  onChange={(e) => setSubscriptionShopFilter(e.target.value)}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white text-gray-900 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Shops</option>
                  {shops.map((shop) => (
                    <option key={shop._id} value={shop._id}>
                      {shop.shopName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Package className="h-4 w-4 text-gray-500" />
                  Status
                </label>
                <select
                  value={subscriptionStatusFilter}
                  onChange={(e) => setSubscriptionStatusFilter(e.target.value)}
                  className="block w-full py-2 px-3 border border-gray-300 bg-white text-gray-900 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Total Records */}
              <div className="flex items-end">
                <div className="w-full">
                  <p className="text-sm font-medium text-gray-700 mb-1">Total Records</p>
                  <p className="text-lg font-semibold text-blue-600">
                    {subscriptionLoading ? <Loader2 className="inline-block animate-spin h-5 w-5" /> : totalRecords}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Tab Content */}
        {activeTab === TAB_INVOICES && (
          <>
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
              Loading invoices...
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shop Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Value
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Gateway Charges
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Commission
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Earnings
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.length > 0 ? (
                      invoices.map((invoice) => (
                        <tr key={invoice._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {invoice.orderId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {Array.isArray(invoice.shopNames) ? invoice.shopNames.join(', ') : (invoice.shops?.[0]?.shopName || invoice.shops?.[0]?.name || 'N/A')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatCurrency(invoice.updatedGrandTotal)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatCurrency(invoice.paymentGatewayCharges)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatCurrency(invoice.commission)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            {formatCurrency(invoice.totalEarnings)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => downloadInvoice(invoice.orderId)}
                              disabled={loadingInvoices.has(invoice.orderId)}
                              className="inline-flex items-center gap-1 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {loadingInvoices.has(invoice.orderId) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                              View Invoice
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                          <p className="text-lg font-medium">No invoices found</p>
                          <p className="text-sm">Try adjusting your filters</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(currentPage - 1) * INVOICES_PER_PAGE + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(currentPage * INVOICES_PER_PAGE, totalRecords)}
                        </span>{' '}
                        of <span className="font-medium">{totalRecords}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          </>
        )}

        {/* Subscription Orders Tab Content */}
        {activeTab === TAB_SUBSCRIPTION_ORDERS && (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {subscriptionLoading ? (
              <div className="p-12 text-center text-gray-500">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                Loading subscription orders...
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plan
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Currency
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created At
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {subscriptionOrders.length > 0 ? (
                        subscriptionOrders.map((order) => (
                          <tr key={order._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {order._id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">
                                  {order.userId?.fullName || 'N/A'}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {order.userId?.email || 'N/A'}
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {order.userId?.phone || 'N/A'}
                                </span>
                                {order.userId?.fullAddress && (
                                  <span className="text-xs text-gray-500 flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {order.userId.fullAddress}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">
                                  {order.planId?.planType || 'N/A'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Period: {order.planId?.period || 'N/A'}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Price: {formatCurrency(order.planId?.price)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Storage: {order.planId?.maxStorageGB || 0}GB
                                </span>
                                <span className="text-xs text-gray-500">
                                  Shops: {order.planId?.maxShopMembers || 0}
                                </span>
                                <span className="text-xs text-gray-500">
                                  Coupons: {order.planId?.maxCouponsPerMonth || 0}/mo
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                order.status === 'active'
                                  ? 'bg-green-100 text-green-800'
                                  : order.status === 'expired'
                                  ? 'bg-red-100 text-red-800'
                                  : order.status === 'cancelled'
                                  ? 'bg-gray-100 text-gray-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {order.status || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {formatCurrency(order.amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {order.currency || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => viewSubscriptionInvoice(order)}
                                  disabled={loadingSubscriptionInvoices.has(order._id)}
                                  className="inline-flex items-center gap-1 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {loadingSubscriptionInvoices.has(order._id) ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                  View
                                </button>
                                <button
                                  onClick={() => downloadSubscriptionInvoicePdf(order)}
                                  disabled={downloadingSubscriptions.has(order._id)}
                                  className="inline-flex items-center gap-1 px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {downloadingSubscriptions.has(order._id) ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                  Download
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                            <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-lg font-medium">No subscription orders found</p>
                            <p className="text-sm">Try adjusting your filters</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing <span className="font-medium">{(currentPage - 1) * INVOICES_PER_PAGE + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(currentPage * INVOICES_PER_PAGE, totalRecords)}
                          </span>{' '}
                          of <span className="font-medium">{totalRecords}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KDPInvoices;
